/**
 * Spreadsheet column detection — shared by BOTH parsers.
 *
 * These are pure functions with no Node dependencies, so the browser can use them
 * too. They were extracted from the old
 * `app/api/leads/parse-excel` route on 2026-09-14, when Excel parsing moved into the
 * browser (like CSV already was) and that route was deleted. Nothing on a server reads
 * a spreadsheet any more.
 *
 * What it does: works out whether row one is a header row, and when it isn't, infers
 * each column's type from its CONTENT (emails, phones, US states, ZIPs, dates of
 * birth) so a headerless export still maps correctly instead of following bad labels.
 */

export const US_STATE_NAMES = [
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut',
  'delaware', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
  'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan',
  'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire',
  'new jersey', 'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio',
  'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
  'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia',
  'wisconsin', 'wyoming', 'district of columbia', 'puerto rico'
];

// Content-based detection patterns to identify data types
export const DATA_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[\d\s\-\(\)\+\.]{7,}$/,
  usStateAbbr: /^(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC|PR)$/i,
  zipCode: /^\d{5}(-\d{4})?$/,
  date: /^(\d{4}-\d{2}-\d{2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})$/,
  // Datetime — a date followed by a time component ("1974-08-31 00:00:00",
  // "1974-08-31T..."). Without this a DOB column exported with a time got
  // detected as nothing (the plain date pattern's $ anchor rejected the time),
  // so DOB silently landed in a custom field instead of the dob field.
  dateTime: /^\d{4}[-/]\d{1,2}[-/]\d{1,2}[ T]\d{1,2}:\d{2}/,
  isoDate: /^\d{4}-\d{2}-\d{2}T/,
  currency: /^\$?[\d,]+(\.\d{2})?$/,
  yesNo: /^(yes|no|y|n|true|false)$/i,
  // Name patterns - single capitalized word (Title-case OR ALL-CAPS), 2-20 chars
  firstName: /^[A-Z][A-Za-z'-]{1,19}$/,
  // City pattern - capitalized words (Title-case OR ALL-CAPS like "GRANT",
  // "KENT CITY"), may have spaces/periods/hyphens ("St. Louis", "Winston-Salem").
  // Lead-vendor exports are frequently ALL-CAPS, so the old Title-case-only
  // pattern silently missed real cities and let them fall into custom fields.
  city: /^[A-Z][A-Za-z.'-]*(\s+[A-Z][A-Za-z.'-]*)*$/,
  // Address pattern - starts with number OR contains street words
  // Matches: "123 Main St", "P.O. Box 123", "Apt 4B 500 Oak Lane", "500 E Oak Street"
  address: /^(\d+\s+[A-Za-z]|P\.?O\.?\s*Box|[A-Za-z]+\s+\d+|.*\b(street|st|avenue|ave|road|rd|drive|dr|lane|ln|way|court|ct|circle|cir|boulevard|blvd|place|pl|terrace|ter|highway|hwy|parkway|pkwy)\b)/i,
  // Gender patterns
  gender: /^(male|female|m|f|other|non-binary)$/i,
  // Marital status patterns
  marital: /^(single|married|divorced|widowed|separated)$/i,
  // Employment status patterns
  employment: /^(employed|unemployed|self-employed|retired|student|disabled)$/i,
  // Smoker status patterns
  smoker: /^(smoker|non-smoker|nonsmoker|yes|no|tobacco|no tobacco)$/i,
  // Insurance type patterns
  insuranceType: /^(burial|term|whole life|universal|final expense|life|health|auto|home)$/i,
  // Beneficiary relationship patterns
  beneficiary: /^(spouse|children|child|family|parent|sibling|other|none)$/i,
};

// Reference year for DOB age-range detection (computed once per request).
export const DOB_REF_YEAR = new Date().getFullYear();

// Strict "looks like a real city name" test — used when COUNTING city matches
// instead of the loose city regex. The broadened ALL-CAPS city regex (needed to
// catch "GRANT"/"FRUITPORT") also swallowed vendor code/abbreviation columns
// ("LWLPMS", "AT", "PD", "G", "P-POS", "TERM ILL", "BILL COD"). Requiring real
// word shape — ≥3 chars per token, contains a vowel, letters only (no digits or
// hyphens), ≤4 tokens — rejects those while still accepting "GRANT",
// "KENT CITY", "St. Louis", "O'Fallon". Verified against real vendor files.
export function looksLikeCityValue(value: string): boolean {
  const s = value.trim();
  if (s.length < 3) return false;
  if (!/^[A-Za-z][A-Za-z .']*$/.test(s)) return false; // letters, space, period, apostrophe only
  const tokens = s.split(/\s+/);
  if (tokens.length > 4) return false;
  return tokens.every(t => t.length >= 3 && /[AEIOU]/i.test(t) && /^[A-Z]/i.test(t));
}

// Check if value is a US state (abbreviation or full name)
export function isUSState(value: string): boolean {
  const cleaned = value.trim().toLowerCase();
  // Check abbreviation
  if (DATA_PATTERNS.usStateAbbr.test(value)) return true;
  // Check full name
  return US_STATE_NAMES.includes(cleaned);
}

// Check if a value looks like typical header text vs actual data
export function looksLikeHeader(value: string | number | boolean | null): boolean {
  if (value === null || value === '') return false;
  const str = String(value).trim();

  // Headers are usually short text without numbers or special patterns
  // Data values often have specific patterns (emails, phones, dates, etc.)

  // If it matches a data pattern, it's likely NOT a header
  if (DATA_PATTERNS.email.test(str)) return false;
  if (DATA_PATTERNS.phone.test(str)) return false;
  if (DATA_PATTERNS.zipCode.test(str)) return false;
  if (DATA_PATTERNS.date.test(str)) return false;
  if (DATA_PATTERNS.isoDate.test(str)) return false;
  if (DATA_PATTERNS.dateTime.test(str)) return false;
  if (isUSState(str)) return false; // State codes or full names are data
  if (DATA_PATTERNS.insuranceType.test(str)) return false; // Insurance types are data
  if (DATA_PATTERNS.beneficiary.test(str)) return false; // Beneficiary values are data

  // If it's just a number (like a phone or zip), it's likely data
  if (/^\d+$/.test(str) && str.length > 2) return false;

  // If it contains @ symbol (email), it's data
  if (str.includes('@')) return false;

  // YES/NO values are data, not headers
  if (DATA_PATTERNS.yesNo.test(str)) return false;

  // Common header words - must contain one of these to be a header
  const headerKeywords = ['name', 'first', 'last', 'email', 'phone', 'address', 'city', 'state', 'zip', 'dob', 'date', 'age', 'status', 'notes', 'column', 'field', 'id', 'type', 'source', 'lead', 'gender', 'marital', 'income', 'occupation', 'smoker', 'beneficiary', 'coverage', 'plan'];
  const lowerStr = str.toLowerCase();
  const hasHeaderKeyword = headerKeywords.some(kw => lowerStr.includes(kw));

  // If it contains a header keyword, it's likely a header
  if (hasHeaderKeyword) return true;

  // Single words that could be names (Title Case, 3-15 chars, no spaces) are likely data
  // e.g., "Edna", "Alexander", "Tuskegee" are data, not headers
  if (/^[A-Z][a-z]{2,14}$/.test(str)) return false;

  // Headers are usually relatively short (under 30 chars) and don't have many spaces
  if (str.length > 30) return false;

  // If it looks like a person's name (Title Case with spaces), it's probably data
  if (/^[A-Z][a-z]+ [A-Z][a-z]+$/.test(str)) return false;

  // If it's a single word without header keywords, assume it's data
  // Headers typically have descriptive names with common keywords
  if (!str.includes(' ') && !str.includes('_') && !hasHeaderKeyword) return false;

  return true;
}

// Detect what type of data a column contains by analyzing sample values
// Returns detected type and confidence score
export function detectColumnType(values: (string | number | boolean | null)[], columnIndex: number): { type: string | null; confidence: number } {
  const validValues = values.filter(v => v !== null && v !== '').map(v => String(v).trim());
  if (validValues.length === 0) return { type: null, confidence: 0 };

  // Count pattern matches for each type
  const counts: Record<string, number> = {
    email: 0,
    phone: 0,
    state: 0,
    zipCode: 0,
    date: 0,
    yesNo: 0,
    firstName: 0,
    city: 0,
    dob: 0,
    address: 0,
    gender: 0,
    marital: 0,
    employment: 0,
    smoker: 0,
    insuranceType: 0,
    beneficiary: 0,
    latitude: 0,
    longitude: 0,
  };

  for (const val of validValues) {
    if (DATA_PATTERNS.email.test(val)) counts.email++;
    // GPS coordinates — high-precision decimals in US lat/long ranges. Detected
    // so the importer can drop them straight into leads.latitude/longitude and
    // Routes uses them (no geocoding). Latitude: +20..50; Longitude: -130..-60
    // (Western hemisphere / continental US). Requires ≥4 decimal places so a
    // 2-decimal dollar amount can't be mistaken for a coordinate.
    if (/^-?\d{1,3}\.\d{4,}$/.test(val)) {
      const num = parseFloat(val);
      if (num >= 20 && num <= 50) counts.latitude++;
      else if (num >= -130 && num <= -60) counts.longitude++;
    }
    // Phone: must LOOK phone-ish AND carry a real phone's worth of digits
    // (10–11). Guards against coordinates ("-79.55"), decimals ("1823.88"),
    // and other numeric junk that the loose phone shape otherwise swallowed.
    if (DATA_PATTERNS.phone.test(val)) {
      const digits = (val.match(/\d/g) || []).length;
      if (digits >= 10 && digits <= 11) counts.phone++;
    }
    if (isUSState(val)) counts.state++; // Use the function that checks both abbr and full names
    if (DATA_PATTERNS.zipCode.test(val)) counts.zipCode++;
    if (DATA_PATTERNS.date.test(val) || DATA_PATTERNS.isoDate.test(val) || DATA_PATTERNS.dateTime.test(val)) {
      counts.date++;
      // DOB = a date whose 4-digit year implies a living adult (age 18–100).
      // Lets us map the birthday column to `dob` directly regardless of its
      // position, instead of the fragile "date column must be after col 8"
      // heuristic that dropped DOBs sitting in an early column (e.g. Column 6).
      const yearMatch = val.match(/(18|19|20)\d{2}/);
      if (yearMatch) {
        const age = DOB_REF_YEAR - parseInt(yearMatch[0], 10);
        if (age >= 18 && age <= 100) counts.dob++;
      }
    }
    if (DATA_PATTERNS.yesNo.test(val)) counts.yesNo++;
    if (DATA_PATTERNS.firstName.test(val)) counts.firstName++;
    if (looksLikeCityValue(val)) counts.city++;
    if (DATA_PATTERNS.address.test(val)) counts.address++;
    if (DATA_PATTERNS.gender.test(val)) counts.gender++;
    if (DATA_PATTERNS.marital.test(val)) counts.marital++;
    if (DATA_PATTERNS.employment.test(val)) counts.employment++;
    if (DATA_PATTERNS.smoker.test(val)) counts.smoker++;
    if (DATA_PATTERNS.insuranceType.test(val)) counts.insuranceType++;
    if (DATA_PATTERNS.beneficiary.test(val)) counts.beneficiary++;
  }

  // Different thresholds for different patterns
  // High-confidence patterns need 50% match, but sparse fields (like address) only need 30%
  const standardThreshold = validValues.length * 0.5; // At least 50% should match
  const sparseThreshold = validValues.length * 0.3; // 30% for fields that are often partially filled

  // Cardinality + fill-rate — used to reject bad City matches. A real city
  // column VARIES (many distinct values) and is mostly filled; a column that is
  // one constant value repeated on every row (e.g. the agent's name stamped on
  // every lead, "Abdullah Altashi") or that is mostly empty is NOT a city even
  // though such values match the (broadened) city pattern.
  const distinctCount = new Set(validValues).size;
  const fillRate = values.length > 0 ? validValues.length / values.length : 0;
  // City needs HIGH confidence (70%): a real city column is ~100% cities, while
  // junk columns that merely contain some word-like values ("TERM ILL" 53%,
  // "BILL COD" 50%) sit well below this and are correctly rejected.
  const cityThreshold = validValues.length * 0.7;

  // Priority order for detection (most specific first)
  // Email is very specific - if it matches, it's definitely email
  if (counts.email >= standardThreshold) return { type: 'email', confidence: counts.email / validValues.length };

  // State codes/names are very specific
  if (counts.state >= standardThreshold) return { type: 'state', confidence: counts.state / validValues.length };

  // Zip codes are specific (5 digits) - check before phone
  if (counts.zipCode >= standardThreshold) return { type: 'zipCode', confidence: counts.zipCode / validValues.length };

  // GPS coordinates — very specific (high-precision decimals in coord ranges),
  // check before phone/date so they aren't swallowed by looser numeric shapes.
  if (counts.longitude >= standardThreshold) return { type: 'longitude', confidence: counts.longitude / validValues.length };
  if (counts.latitude >= standardThreshold) return { type: 'latitude', confidence: counts.latitude / validValues.length };

  // Date of birth — a date column whose values are birthdates (living-adult age
  // range). Checked BEFORE the generic `date` type so the birthday column maps
  // straight to `dob`. Non-DOB dates (policy/created dates in recent years) fall
  // through to `date`.
  if (counts.dob >= standardThreshold) return { type: 'dob', confidence: counts.dob / validValues.length };

  // Dates - check BEFORE phone because date patterns can look like phone patterns
  // Dates with dashes (2025-09-12, 1954-05-19) could also match phone pattern
  if (counts.date >= standardThreshold) return { type: 'date', confidence: counts.date / validValues.length };

  // Phone numbers - after date to avoid date misdetection
  if (counts.phone >= standardThreshold) return { type: 'phone', confidence: counts.phone / validValues.length };

  // Address - use sparse threshold since addresses are often partially filled
  if (counts.address >= sparseThreshold) return { type: 'address', confidence: counts.address / validValues.length };

  // Gender
  if (counts.gender >= standardThreshold) return { type: 'gender', confidence: counts.gender / validValues.length };

  // Marital status
  if (counts.marital >= standardThreshold) return { type: 'marital', confidence: counts.marital / validValues.length };

  // Employment status
  if (counts.employment >= standardThreshold) return { type: 'employment', confidence: counts.employment / validValues.length };

  // Smoker status
  if (counts.smoker >= standardThreshold) return { type: 'smoker', confidence: counts.smoker / validValues.length };

  // Insurance type (burial, term, etc.)
  if (counts.insuranceType >= standardThreshold) return { type: 'insuranceType', confidence: counts.insuranceType / validValues.length };

  // Beneficiary relationship
  if (counts.beneficiary >= standardThreshold) return { type: 'beneficiary', confidence: counts.beneficiary / validValues.length };

  // Yes/No values
  if (counts.yesNo >= standardThreshold) return { type: 'yesNo', confidence: counts.yesNo / validValues.length };

  // Names and cities are tricky - they both match capitalized words
  // Use column position as a hint for typical lead file formats
  // Column 2 is often firstName, Column 3 is often lastName
  if (counts.firstName >= sparseThreshold) {
    // If column 2 and looks like names, it's likely firstName
    if (columnIndex === 1) return { type: 'firstName', confidence: counts.firstName / validValues.length };
    // If column 3 and looks like names, it's likely lastName
    if (columnIndex === 2) return { type: 'lastName', confidence: counts.firstName / validValues.length };
  }

  // City detection - usually comes after address or before state
  // If it matches city pattern and we haven't identified it as a name.
  // Guards: distinctCount > 1 rejects a constant column (agent name repeated on
  // every row); fillRate >= 0.5 rejects mostly-empty columns — both were being
  // mislabeled "(City)" purely because their sparse values matched the pattern.
  if (counts.city >= cityThreshold && columnIndex > 3 && distinctCount > 1 && fillRate >= 0.5) {
    return { type: 'city', confidence: counts.city / validValues.length };
  }

  return { type: null, confidence: 0 };
}

// Advanced column type detection using position heuristics
// Common lead file format: Date, FirstName, LastName, Email, Phone, [Flag], Address, City, State, Zip, DOB, ...
export function detectColumnTypeWithPosition(
  values: (string | number | boolean | null)[],
  columnIndex: number,
  totalColumns: number,
  allColumnsDetected: Record<number, { type: string | null; confidence: number }>
): { type: string | null; confidence: number } {
  // First, try content-based detection
  const contentResult = detectColumnType(values, columnIndex);

  // If we got a high-confidence result, use it
  if (contentResult.confidence >= 0.7) {
    return contentResult;
  }

  const validValues = values.filter(v => v !== null && v !== '').map(v => String(v).trim());
  if (validValues.length === 0) return { type: null, confidence: 0 };

  // Use position-based heuristics for common patterns
  // Check if values look like names (capitalized single words)
  const looksLikeName = validValues.every(v => DATA_PATTERNS.firstName.test(v));

  if (looksLikeName) {
    // Column 2 (index 1) is often firstName in lead files
    if (columnIndex === 1 && !allColumnsDetected[1]?.type) {
      return { type: 'firstName', confidence: 0.8 };
    }
    // Column 3 (index 2) is often lastName
    if (columnIndex === 2 && !allColumnsDetected[2]?.type) {
      return { type: 'lastName', confidence: 0.8 };
    }
  }

  // Check for city - usually after address/phone and before state
  const looksLikeCity = validValues.every(v => looksLikeCityValue(v));
  if (looksLikeCity) {
    // If next column is state, this is likely city
    const nextColType = allColumnsDetected[columnIndex + 1]?.type;
    if (nextColType === 'state') {
      return { type: 'city', confidence: 0.9 };
    }
    // If position is typical for city (column 7-9 range)
    if (columnIndex >= 6 && columnIndex <= 9) {
      return { type: 'city', confidence: 0.6 };
    }
  }

  return contentResult;
}

/**
 * ONE workbook at a time, per instance.
 *
 * The 3MB cap bounds a SINGLE parse to roughly 120MB of heap on top of a ~240MB
 * baseline — comfortable against the 400MB ceiling. Two at once is 480MB and
 * kills the process, taking every other request on that instance with it.
 * That is not hypothetical with 474 active users behind two instances.
 *
 * A rejected upload is a retry. A dead instance is an outage for everyone.
 *
 * In-process on purpose: this guards THIS instance's heap, which is the thing
 * that dies. Render load-balances across two, so a shared lock would be both
 * unnecessary and a false constraint.
 */
let parseInFlight = false;
